"use client"

/**
 * StickyBannerAd — a small display banner pinned to a fixed screen corner,
 * sitewide (mounted once in app/layout.tsx, unlike the other display
 * placements which are per-modal/per-video). Reuses the same "render the
 * creative at its true native size inside a sandboxed cross-origin iframe"
 * approach as NetworkDisplayAd/LetterboxAd/PillarboxAd — see the sizing
 * history documented on NetworkDisplayAd for why that matters (a fixed-size
 * iframe forced into an arbitrary box overflows and grows scrollbars).
 * Unlike those, this isn't fit into a column of unknown width — it's a
 * standalone floating box, so it just renders at native size directly, no
 * scale-to-fit math needed.
 *
 * AUTO_DISMISS_MS — see the matching comment on InstantMessageAd.tsx for the
 * full reasoning (no expiry at all previously, "even when it goes away, they
 * just sit there"). Longest of the four sitewide widgets: unlike the
 * notification/video-slider formats, a static display banner has no
 * animate-in/out behavior of its own that would signal it's "done," so this
 * is a pure safety net rather than a bound matched to the creative's own
 * lifecycle — set generously so it doesn't cut off a still-live banner.
 */
const AUTO_DISMISS_MS = 90_000

import { useEffect, useRef, useState } from "react"
import { X } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useVRStore } from "@/lib/vr-store"
import { useAdTier } from "@/lib/ad-tier"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"
import { isSkipAdsModalActive } from "@/lib/skip-ads"
import { useCornerAdPlacement } from "@/lib/ad-placement"
import { useFixedCornerAdScale } from "@/lib/hooks/use-fixed-corner-ad-scale"
import { useTranslations } from "next-intl"

interface AdData {
  zone_id: string
  zone_type: string
  embed_code: string | null
  creative_width: number | null
  creative_height: number | null
}

export function StickyBannerAd() {
  const t = useTranslations("networkDisplayAd")
  const hasConsent = useHasConsent()
  // Suppressed entirely while in active VR mode (app/vr/page.tsx) — a fixed
  // corner widget popping up over a full-screen VR view has no sensible
  // place to render and would sit on top of the video.
  const isVRMode = useVRStore((s) => s.isVRMode)
  const { isPremiumUser } = useAdStore()
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this widget to show for its only realistic tester.
  // SkipAdsModal forces this widget on via triggerStickyBanner() +
  // isSkipAdsModalActive() (see below), but that bypass only ever covered
  // the "is this ad type enabled" check — this premium gate had no matching
  // bypass. Reported live: on a real premium/admin account testing
  // SkipAdsModal WITHOUT Force Ads also happening to be on in that exact
  // tab, all 4 corner widgets silently rendered nothing even though real
  // ad_zones inventory existed and the modal genuinely triggered them — the
  // whole point of the modal ("here are all the ads, right now") shouldn't
  // depend on Force Ads state at all.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn() && !isSkipAdsModalActive()
  const adTier = useAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)
  const [dismissed, setDismissed] = useState(false)
  // AdTriggerOverlay's manual-fire button — see lib/ad-trigger-store.ts.
  // Bumping this counter re-runs the fetch below with the cooldown check
  // skipped for that one call, so an admin can force-show this on demand
  // instead of waiting out the real cooldown.
  const triggerCount = useAdTriggerStore((s) => s.stickyBanner)
  const lastTriggerRef = useRef(0)
  // Only allowed on the pages lib/ad-placement.ts lists. A manual harness
  // trigger or the Skip Ads modal shows it anywhere.
  const placementOk = useCornerAdPlacement("sticky_banner")
  const forcedRef = useRef(false)

  useEffect(() => {
    const manuallyTriggered = triggerCount > lastTriggerRef.current
    lastTriggerRef.current = triggerCount
    if (manuallyTriggered) forcedRef.current = true
    if (!hasConsent || effectivePremiumUser || isVRMode) return
    if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return
    // isSkipAdsModalActive() lets the Skip-Ads reward modal force this on
    // even while it's disabled sitewide (enabled: false — no agreed home for
    // this widget yet) — see lib/skip-ads.ts's own comment. A manual trigger
    // from the admin ad harness is the same idea: previewing a currently
    // disabled placement is the entire point of that panel, so it bypasses
    // this too, not just the cooldown check below.
    // The on/off switch and cooldown must be read AFTER the real config has
    // loaded. This used to check them synchronously at mount, before anything
    // on the page had fetched the config, so it fell back to the hard-coded
    // DEFAULT_CONFIG (which has this on) and showed up even while switched off
    // in admin, on any page that hadn't loaded the config yet.
    let ignore = false
    useAdFrequencyStore.getState().fetchConfig().then(() => {
      if (ignore) return
      if (!manuallyTriggered && !useAdFrequencyStore.getState().isEnabled("sticky_banner_ads") && !isSkipAdsModalActive()) return
      if (!manuallyTriggered) {
        const cooldown = useAdFrequencyStore.getState().getCooldownMinutes("sticky_banner_ads")
        if (!isAdCooldownExpired("sticky_banner", cooldown)) return
      }
      if (manuallyTriggered) setDismissed(false)

      fetch(`/api/ads/network-grid-ad?type=sticky_banner&tier=${adTier}${isAdTestModeOn() ? "&preview=1" : ""}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (ignore) return
          if (d?.zone_id) {
            setAdData(d)
            markAdShown("sticky_banner")
          }
        })
        .catch(() => {})
    })
    return () => {
      ignore = true
    }
  }, [hasConsent, effectivePremiumUser, adTier, triggerCount, placementOk])

  useEffect(() => {
    if (!adData) return
    const timer = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [adData])

  // Computed before the early return below (and with a fallback for a null
  // adData) so the scale hook — which must run unconditionally, same as
  // every hook — always has a real number to work with on every render.
  const nativeW = adData?.creative_width ?? 300
  const nativeH = adData?.creative_height ?? 250
  const { scale, scaledWidth, scaledHeight } = useFixedCornerAdScale(nativeW, nativeH)

  if (!hasConsent || effectivePremiumUser || isVRMode || !adData || dismissed) return null
  if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return null

  const zoneId = adData.zone_type === "js_embed" ? extractExoclickZoneId(adData.embed_code) : null

  // sticky_banner_ads is a shared pool now: ExoClick's zone (a real
  // src=-loadable magsrv.com URL, matched above via extractExoclickZoneId)
  // competes for the same corner slot with ClickAdilla's (a
  // `<script data-admpid>` + `<div data-banner-id>` pair with no equivalent
  // URL to point a real src= at — see NetworkDisplayAd.tsx's history for why
  // that matters). One random pick per impression between the two, same
  // randomization every other placement's zone-selection already does.
  //
  // ClickAdilla's script only needs to find its OWN target div somewhere in
  // the SAME document it executes in — that document doesn't have to be our
  // real page. Rendering both tags together inside one sandboxed srcDoc
  // iframe (no allow-same-origin, matching the interstitial's own
  // ClickAdilla fallback) gets it a div to fill without ever running
  // unsandboxed in our real DOM.
  const isClickAdillaBanner = !zoneId && adData.zone_type === "js_embed" && !!adData.embed_code?.includes("data-banner-id")

  if (!zoneId && !isClickAdillaBanner) return null

    // top-4 left-4. Originally moved off bottom-right because that corner
    // belonged to InstantMessageAd, after both were confirmed live stacked
    // on top of each other with one hiding most of the other. This banner
    // has no positional convention of its own to protect, so it was the one
    // that moved, and it stays here.
    //
    // Bottom-right has since gone to VideoSliderAd instead (2026-09-13): its
    // ExoClick creative animates in from the right edge, so a left-hand
    // container made it look like it was sliding in from the wrong side.
    // InstantMessageAd moved to bottom-left to make room — a real tradeoff,
    // since its "looks like an incoming chat message" premise leans on the
    // conventional bottom-right spot real chat widgets use. A visibly wrong
    // animation direction won over a softer stylistic convention.
  return (
    <div
      className="fixed top-4 left-4 z-40 overflow-hidden rounded-lg border border-border/30 bg-muted/20 shadow-xl pointer-events-auto"
      style={{ width: scaledWidth, height: scaledHeight }}
    >
      <Badge
        variant="outline"
        className="absolute top-1.5 left-1.5 z-10 text-[9px] px-1 py-0 bg-black/70 text-white/80 border-0"
      >
        {t("ad")}
      </Badge>
      {/* Unlike its 3 siblings (InPagePushAd, InstantMessageAd,
          VideoSliderAd), this zone's creative does not reliably paint its
          own close button — confirmed live across multiple rotations, some
          filled the full 300x250 box with no visible close control at all.
          Plain black/70 to match the network's own close-button convention
          (not the brand purple used on Interstitial/PauseOverlayAd, which
          are our own modal chrome, not a stand-in for a missing network
          control). */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute bottom-1.5 right-1.5 z-10 h-5 w-5 rounded-full bg-black/70 hover:bg-black/90 text-white/70 hover:text-white flex items-center justify-center transition-colors"
        aria-label={t("closeAd")}
      >
        <X className="h-3 w-3" />
      </button>
      {/* scale === 1 (desktop) renders exactly as before this mobile fix —
          width/height:100%, no transform. See the matching comment in
          VideoSliderAd.tsx for why that risk isn't worth carrying on the
          common path where no shrink is even needed. Below scale 1, the
          iframe renders at NATIVE nativeW/nativeH and gets CSS-transformed
          down to match the wrapper (which IS sized to the scaled
          dimensions) — same scale-to-fit technique as NetworkDisplayAd. */}
      {isClickAdillaBanner ? (
        <iframe
          srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${adData.embed_code}</body></html>`}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
          title={t("advertisement")}
          scrolling="no"
          style={
            scale >= 1
              ? { width: "100%", height: "100%", border: 0, display: "block" }
              : { width: nativeW, height: nativeH, border: 0, display: "block", transform: `scale(${scale})`, transformOrigin: "top left" }
          }
        />
      ) : (
        <iframe
          src={exoclickIframeSrc(zoneId!)}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          title={t("advertisement")}
          scrolling="no"
          style={
            scale >= 1
              ? { width: "100%", height: "100%", border: 0, display: "block" }
              : { width: nativeW, height: nativeH, border: 0, display: "block", transform: `scale(${scale})`, transformOrigin: "top left" }
          }
        />
      )}
    </div>
  )
}
